/**
 * getPeriodRange.js
 *
 * Converts { view, year, periodIndex } from TimeNavigator
 * into { periodFrom, periodTo } as "YYYY-MM-DD" strings.
 *
 * view = "year"     → full year
 * view = "halfyear" → H1/H2        (periodIndex 0-1)
 * view = "tertial"  → T1/T2/T3     (periodIndex 0-2)
 * view = "quarter"  → Q1/Q2/Q3/Q4  (periodIndex 0-3)
 * view = "month"    → Jan-Dec       (periodIndex 0-11)
 * view = "week"     → ISO week 1-53 (periodIndex 0-52) — Mon to Sun
 */

/**
 * @param {string} view        - "year" | "halfyear" | "tertial" | "quarter" | "month" | "week"
 * @param {number} year        - e.g. 2025
 * @param {number} periodIndex - 0-based index within the view
 * @returns {{ periodFrom: string, periodTo: string }}
 */
export function getPeriodRange(view, year, periodIndex) {
  switch (view) {
    case "year":
      return {
        periodFrom: `${year}-01-01`,
        periodTo:   `${year}-12-31`,
      };

    case "quarter": {
      // Q1=0: Jan-Mar, Q2=1: Apr-Jun, Q3=2: Jul-Sep, Q4=3: Oct-Dec
      const quarterStarts = [
        [1, 1],   // Q1
        [4, 1],   // Q2
        [7, 1],   // Q3
        [10, 1],  // Q4
      ];
      const quarterEnds = [
        [3, 31],  // Q1
        [6, 30],  // Q2
        [9, 30],  // Q3
        [12, 31], // Q4
      ];
      const idx = Math.min(Math.max(periodIndex, 0), 3);
      const [fromM, fromD] = quarterStarts[idx];
      const [toM, toD]     = quarterEnds[idx];
      return {
        periodFrom: formatDate(year, fromM, fromD),
        periodTo:   formatDate(year, toM, toD),
      };
    }

    case "tertial": {
      // T1=0: Jan-Apr, T2=1: May-Aug, T3=2: Sep-Dec
      const tertialStarts = [[1, 1], [5, 1], [9, 1]];
      const tertialEnds   = [[4, 30], [8, 31], [12, 31]];
      const idx = Math.min(Math.max(periodIndex, 0), 2);
      const [fromM, fromD] = tertialStarts[idx];
      const [toM, toD]     = tertialEnds[idx];
      return {
        periodFrom: formatDate(year, fromM, fromD),
        periodTo:   formatDate(year, toM, toD),
      };
    }

    case "halfyear": {
      // H1=0: Jan-Jun, H2=1: Jul-Dec
      const idx = Math.min(Math.max(periodIndex, 0), 1);
      return idx === 0
        ? { periodFrom: formatDate(year, 1, 1),  periodTo: formatDate(year, 6, 30) }
        : { periodFrom: formatDate(year, 7, 1),  periodTo: formatDate(year, 12, 31) };
    }

    case "month": {
      // periodIndex 0=Jan, 1=Feb, ..., 11=Dec
      const idx = Math.min(Math.max(periodIndex, 0), 11);
      const month = idx + 1;
      const lastDay = new Date(year, month, 0).getDate(); // day 0 of next month = last day of current
      return {
        periodFrom: formatDate(year, month, 1),
        periodTo:   formatDate(year, month, lastDay),
      };
    }

    case "week": {
      // periodIndex 0 = Week 1 (ISO: first week with Thursday in the year)
      // We use a simpler approach: Week 1 starts on Jan 1, each week is Mon-Sun
      // periodIndex 0-51 = weeks 1-52, periodIndex 52 = week 53 if it exists
      const weekNum = periodIndex + 1; // 1-based

      // Get the Monday of ISO week `weekNum` of `year`
      const { monday, sunday } = getISOWeekRange(year, weekNum);
      return {
        periodFrom: toDateString(monday),
        periodTo:   toDateString(sunday),
      };
    }

    default:
      return {
        periodFrom: `${year}-01-01`,
        periodTo:   `${year}-12-31`,
      };
  }
}

/**
 * Index of the period that CONTAINS TODAY, for a given view and year.
 *
 * Returns 0 for any other year, so browsing 2025 still starts at its first
 * period. Used to open the dashboard on live data (and to keep a view switch
 * on the current period) instead of always landing on January / Q1.
 *
 * @param {string} view
 * @param {number} year
 * @returns {number} 0-based period index
 */
export function getCurrentPeriodIndex(view, year) {
  const now = new Date();
  if (year !== now.getFullYear()) return 0;
  const m = now.getMonth(); // 0 = January
  switch (view) {
    case "month":    return m;                    // 0-11
    case "quarter":  return Math.floor(m / 3);    // Q1..Q4
    case "tertial":  return Math.floor(m / 4);    // T1: Jan-Apr, T2: May-Aug, T3: Sep-Dec
    case "halfyear": return m < 6 ? 0 : 1;        // H1 / H2
    case "week":     return Math.min(Math.max(getISOWeekNumber(now) - 1, 0), 52);
    default:         return 0;                    // "year" ignores periodIndex
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────

/** ISO-8601 week number (1-53) of `date`. The Thursday of the week decides
 *  which ISO year/week it belongs to — matches getISOWeekRange above. */
function getISOWeekNumber(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay() || 7;        // Mon=1 ... Sun=7
  d.setDate(d.getDate() + 4 - day);   // shift to this week's Thursday
  const jan1 = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - jan1) / 86400000 + 1) / 7);
}

function formatDate(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function toDateString(date) {
  return formatDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/**
 * Returns Monday and Sunday of ISO week `weekNum` of `year`.
 * ISO week 1 = the week containing the first Thursday of the year.
 */
function getISOWeekRange(year, weekNum) {
  // Jan 4 is always in ISO week 1
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7; // Mon=1 ... Sun=7
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - (dayOfWeek - 1));

  const monday = new Date(week1Monday);
  monday.setDate(week1Monday.getDate() + (weekNum - 1) * 7);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  return { monday, sunday };
}
